/**
 * Governance Hub detail panel — the slide-over where one item gets full depth.
 *
 * The list rows answer "what and when"; everything else happens here: the
 * contract's own language and section, the owner (with the assignment
 * controls folded behind "Change"), emailing the owner, the working thread,
 * completion, and AI guidance. One item in focus, the list undisturbed
 * behind it — the same progressive-disclosure shape the mandate app uses.
 */
import React, { useEffect, useState } from 'react';
import ThreadPanel from './ThreadPanel';
import DeliverablesSection from './DeliverablesSection';

const API = process.env.REACT_APP_API_URL;

/**
 * Manual calendar notice — compliance picks the recipients and triggers the
 * send; the calendar never emails on its own. Mandate-format email built
 * server-side; this section is just the trigger.
 */
function SendEmailSection({ item }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState({});
  const [filter, setFilter] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');

  useEffect(() => {
    if (!open || users.length) return;
    fetch(`${API}/api/calendar/assignable-users`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d) => setUsers(Array.isArray(d.users) ? d.users : []))
      .catch(() => {});
  }, [open, users.length]);

  const pickedIds = Object.keys(picked).filter((k) => picked[k]);
  const shown = users.filter((u) => !filter
    || (u.name || '').toLowerCase().includes(filter.toLowerCase())
    || (u.email || '').toLowerCase().includes(filter.toLowerCase()));

  const send = async () => {
    setBusy(true); setResult('');
    try {
      const res = await fetch(`${API}/api/calendar/obligations/${item.id}/notify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_user_ids: pickedIds, note: note.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Send failed');
      const sent = (data.results || []).filter((x) => x.sent).length;
      setResult(`Sent to ${sent} recipient${sent === 1 ? '' : 's'}.`);
      setPicked({}); setNote('');
    } catch (e) {
      setResult(e.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl bg-orange-50/60 border border-orange-200 p-4">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left">
        <span className="text-xs font-semibold uppercase tracking-wider text-orange-800">Email this item</span>
        <span className="text-orange-700 text-sm">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-slate-500">
            You choose who receives it — the calendar never emails automatically.
          </p>
          <input
            type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter people…"
            className="w-full text-sm bg-white border border-slate-200 rounded-lg px-3 py-1.5"
          />
          <div className="max-h-40 overflow-y-auto bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {shown.map((u) => (
              <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox" checked={!!picked[u.id]}
                  onChange={(e) => setPicked((p) => ({ ...p, [u.id]: e.target.checked }))}
                  className="rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="truncate">{u.name || u.email}</span>
                {u.name && u.email && <span className="text-xs text-slate-400 truncate">{u.email}</span>}
              </label>
            ))}
            {!shown.length && <div className="px-3 py-2 text-xs text-slate-400">No people match.</div>}
          </div>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="Optional note — appears in the email"
            className="w-full text-sm bg-white border border-slate-200 rounded-lg px-3 py-2"
          />
          <div className="flex items-center gap-3">
            <button
              type="button" onClick={send} disabled={busy || pickedIds.length === 0}
              className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              {busy ? 'Sending…' : `Send email${pickedIds.length ? ` (${pickedIds.length})` : ''}`}
            </button>
            {result && <span className="text-sm text-slate-600">{result}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

const SOURCE_STYLES = {
  memo: 'bg-violet-100 text-violet-700 border-violet-200',
  cap: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  calendar: 'bg-sky-100 text-sky-700 border-sky-200',
};

const sourceLabel = (s, item) => {
  const n = (s || '').toLowerCase();
  if (n === 'memo') return 'Memo';
  if (n === 'cap') return 'CAP';
  // Calendar obligations: label by program, not a blanket "CMS".
  const program = item?.obligation_type || item?.type || '';
  if (program === 'State Medicaid (SMAC)') return 'Medicaid';
  if (program === 'CMS') return 'Medicare';
  return program || 'Calendar';
};

export default function DetailPanel({
  item,
  onClose,
  onOpenInEmbeddedView,
  formatDateDisplay,
  effectiveStatus,
  statusStyle,
  effectiveStatusLabel,
  calendarStatusLabel,
  isCalendarNonActionable,
  isCompliance,
  renderObligationAccountable,
  handleStatusChange,
  handleActionableChange,
  handleFetchGuidance,
  statusUpdatingId,
  actionableUpdatingId,
  guidanceByObligationId,
  isCalendarObligationCompleted,
  onObligationUpdated,
}) {
  const [editOwner, setEditOwner] = useState(false);
  const [remindState, setRemindState] = useState(null); // null | 'busy' | 'sent' | error string

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // A fresh item gets a fresh panel — no leftover edit state from the last one.
  useEffect(() => { setEditOwner(false); setRemindState(null); }, [item?.id]);

  if (!item) return null;

  const source = item.source || 'calendar';
  const regulator = item.authority || item.obligation_type || item.type || item.classification;
  const guidance = guidanceByObligationId?.[item.id];
  const isMemo = source === 'memo';
  const isCap = source === 'cap';
  const isContract = !!item.smac_obligation_id;

  const sendReminder = async () => {
    if (remindState === 'busy') return;
    setRemindState('busy');
    try {
      const r = await fetch(`${API}/api/smac/obligations/${item.smac_obligation_id}/remind`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      setRemindState(d.success ? 'sent' : (d.message || 'The reminder could not be sent.'));
    } catch {
      setRemindState('The reminder could not be sent.');
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/30 z-40 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close panel"
      />
      <aside
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto border-l border-slate-200"
        role="dialog"
        aria-labelledby="detail-panel-title"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 id="detail-panel-title" className="text-lg font-semibold text-slate-800">Item details</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <span className={`inline-flex px-2 py-0.5 rounded-lg text-[10px] font-semibold uppercase border ${SOURCE_STYLES[source] || SOURCE_STYLES.calendar}`}>
              {sourceLabel(source, item)}
            </span>
            {item.visibility_reason && (
              <span className="ml-2 text-xs text-slate-500 italic">— {item.visibility_reason}</span>
            )}
          </div>

          <h3 className="text-lg font-semibold text-slate-800">{(item.title && String(item.title).trim()) || '—'}</h3>

          <dl className="grid grid-cols-1 gap-3 text-sm">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Due date</dt>
              <dd className="text-slate-800 mt-0.5">{formatDateDisplay(item)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Status</dt>
              <dd className="mt-0.5">
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${statusStyle(effectiveStatus)}`}>
                  {isCap ? effectiveStatusLabel(effectiveStatus) : calendarStatusLabel?.(item) ?? effectiveStatusLabel(effectiveStatus)}
                </span>
                {isCalendarNonActionable?.(item) && (
                  <span className="ml-2 inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">Non-actionable</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Type</dt>
              <dd className="text-slate-800 mt-0.5">{regulator || '—'}</dd>
            </div>
            {isContract && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Required by</dt>
                <dd className="mt-1">
                  <div className="text-sm text-slate-800">
                    <span className="font-medium">{item.contract_name || 'State Medicaid contract'}</span>
                    {item.contract_state ? <span className="text-slate-500"> ({item.contract_state})</span> : null}
                    {item.contract_section ? (
                      <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-semibold align-middle">
                        {item.contract_section}
                      </span>
                    ) : null}
                  </div>
                  {item.contract_duty && (
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{item.contract_duty}</p>
                  )}
                  {item.contract_quote && (
                    <blockquote className="mt-2 border-l-2 border-indigo-300 bg-indigo-50/50 pl-3 pr-2 py-2 text-[13px] text-slate-700 italic leading-relaxed rounded-r">
                      “{item.contract_quote}”
                    </blockquote>
                  )}
                </dd>
              </div>
            )}
            {!isContract && item.source === 'calendar' && (item.source_text || item.source_reference) && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Source</dt>
                <dd className="mt-1">
                  {item.source_reference && (
                    <div className="text-sm text-slate-800 font-medium break-words">{item.source_reference}</div>
                  )}
                  {item.source_text && (
                    <details className="mt-1.5">
                      <summary className="cursor-pointer text-[13px] font-medium text-blue-700 hover:text-blue-900 select-none">
                        See what the calendar says
                      </summary>
                      <blockquote className="mt-2 border-l-2 border-blue-300 bg-blue-50/50 pl-3 pr-2 py-2 text-[13px] text-slate-700 italic leading-relaxed rounded-r whitespace-pre-wrap">
                        “{item.source_text}”
                      </blockquote>
                    </details>
                  )}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Accountable</dt>
              <dd className="text-slate-800 mt-0.5">
                {isMemo ? (
                  (Array.isArray(item.departments) && item.departments.length > 0) ? item.departments.join(', ') : '—'
                ) : isCap ? (
                  item.owner_name || item.owner_email || '—'
                ) : isContract && !editOwner ? (
                  <div className="text-sm">
                    {(item.contract_owner_name || item.assignee_name) ? (
                      <>
                        <span className="block font-medium">{item.contract_owner_name || item.assignee_name}</span>
                        {(item.contract_owner_email || item.assignee_email) && (
                          <span className="block text-slate-500 font-mono text-xs break-all">{item.contract_owner_email || item.assignee_email}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400">No owner yet</span>
                    )}
                    {isCompliance && (
                      <button type="button" onClick={() => setEditOwner(true)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium mt-1 block">
                        Change
                      </button>
                    )}
                  </div>
                ) : (
                  <div>
                    {renderObligationAccountable?.(item, false)}
                    {isContract && (
                      <button type="button" onClick={() => setEditOwner(false)}
                        className="text-xs text-slate-500 hover:text-slate-700 font-medium mt-1 block">
                        Done
                      </button>
                    )}
                  </div>
                )}
              </dd>
            </div>
            {(isMemo || isCap) && (item.owner_email || item.owner_name) && (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Owner</dt>
                <dd className="text-slate-800 mt-0.5">
                  {item.owner_name && <span className="block">{item.owner_name}</span>}
                  {item.owner_email && <span className="block text-slate-600 font-mono text-xs break-all">{item.owner_email}</span>}
                </dd>
              </div>
            )}
          </dl>

          {!isMemo && !isCap && source === 'calendar' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">Actionable</label>
                <select
                  value={item.actionable === false ? 'no' : 'yes'}
                  onChange={(e) => handleActionableChange?.(item.id, e.target.value === 'yes')}
                  disabled={actionableUpdatingId === item.id}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">Mark complete</label>
                <button
                  type="button"
                  onClick={() => handleStatusChange?.(item.id, isCalendarObligationCompleted?.(item) ? 'upcoming' : 'completed')}
                  disabled={statusUpdatingId === item.id}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border-2 ${
                    isCalendarObligationCompleted?.(item) ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'bg-white border-slate-300 text-slate-600 hover:border-slate-400'
                  }`}
                >
                  {isCalendarObligationCompleted?.(item) ? 'Completed' : 'Mark complete'}
                </button>
              </div>
            </div>
          )}

          {!isMemo && !isCap && source === 'calendar' && (
            <DeliverablesSection
              item={item}
              isCompleted={!!isCalendarObligationCompleted?.(item)}
              onObligationUpdated={onObligationUpdated}
            />
          )}

          {isCompliance && source === 'calendar' && (
            <SendEmailSection item={item} />
          )}

          {item.ai_guidance_status === 'provided' && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">AI guidance</h4>
              {item.ai_guidance_actions?.length > 0 && (
                <ul className="list-disc list-inside text-slate-700 text-sm space-y-1">
                  {item.ai_guidance_actions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              )}
              {item.ai_guidance_by_whom && <p className="text-sm font-medium mt-2">By whom: {item.ai_guidance_by_whom}</p>}
              {item.ai_guidance_message && !item.ai_guidance_actions?.length && <p className="text-slate-600 text-sm">{item.ai_guidance_message}</p>}
            </div>
          )}
          {!item.ai_guidance_status && source === 'calendar' && (
            <button
              type="button"
              onClick={() => handleFetchGuidance?.(item.id)}
              disabled={guidance?.loading}
              className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-medium"
            >
              {guidance?.loading ? 'Loading…' : 'Get AI guidance'}
            </button>
          )}

          {isMemo && item.memo_url && (
            <button
              type="button"
              onClick={() => onOpenInEmbeddedView?.(item.memo_url)}
              className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-800 font-medium text-sm"
            >
              Open memo →
            </button>
          )}
          {isCap && item.cap_url && (
            <button
              type="button"
              onClick={() => onOpenInEmbeddedView?.(item.cap_url)}
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-800 font-medium text-sm"
            >
              Open CAP →
            </button>
          )}

          {isMemo && Array.isArray(item.action_items) && item.action_items.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Action items</h4>
              <ul className="list-disc list-inside text-slate-700 text-sm space-y-1">
                {item.action_items.map((text, i) => <li key={i}>{text}</li>)}
              </ul>
            </div>
          )}

          {isContract && (
            <div className="border-t border-slate-200 pt-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Work it with the owner</h4>
              {isCompliance && (item.contract_owner_email || item.assignee_email) && (
                remindState === 'sent' ? (
                  <p className="text-sm text-emerald-600 font-medium mb-1">Reminder sent to the owner.</p>
                ) : (
                  <button type="button" onClick={sendReminder} disabled={remindState === 'busy'}
                    title="Email the owner about this item now — the same reminder the schedule sends, and it lands in the thread"
                    className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-medium disabled:opacity-50 mb-1">
                    {remindState === 'busy' ? 'Sending…' : 'Email the owner'}
                  </button>
                )
              )}
              {remindState && remindState !== 'busy' && remindState !== 'sent' && (
                <p className="text-xs text-red-600 mb-1">{remindState}</p>
              )}
              <ThreadPanel obligationId={item.smac_obligation_id} />
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
